import { Component, OnInit, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators, FormArray } from '@angular/forms';
import { ModuleConfig, DepartmentItem, AssistantItem } from '../../../../models/customer-data.model';
import { CustomerManagerService } from '../../../../services/customer-manager.service';

@Component({
  selector: 'app-module-management',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule
  ],
  templateUrl: './module-management.component.html',
  styleUrl: './module-management.component.scss'
})
export class ModuleManagementComponent implements OnInit {

  // 事件發射器
  @Output() moduleUpdated = new EventEmitter<void>();

  // 資料與狀態
  moduleConfigs: ModuleConfig[] = [];
  filteredModuleConfigs: ModuleConfig[] = [];
  selectedModuleConfig: ModuleConfig | null = null;
  isEditing = false;
  isLoading = false;
  showModal = false;

  // 表單
  moduleForm: FormGroup;

  // 搜尋與分頁
  searchTerm = '';
  currentPage = 1;
  itemsPerPage = 10;
  totalItems = 0;

  constructor(
    private fb: FormBuilder,
    private customerManagerService: CustomerManagerService
  ) {
    this.moduleForm = this.createForm();
  }

  ngOnInit(): void {
    this.loadModuleConfigs();
  }

  /**
   * 建立表單
   */
  createForm(): FormGroup {
    return this.fb.group({
      companyid: ['', [Validators.required]],
      show_nopermission: [false],
      departments_introduce: ['', [Validators.required]],
      departments: this.fb.array([]),
      lobby: this.fb.array([])
    });
  }

  /**
   * 獲取 departments FormArray
   */
  get departmentsFormArray(): FormArray {
    return this.moduleForm.get('departments') as FormArray;
  }

  /**
   * 獲取 lobby FormArray
   */
  get lobbyFormArray(): FormArray {
    return this.moduleForm.get('lobby') as FormArray;
  }

  /**
   * 載入模組配置資料
   */
  loadModuleConfigs(): void {
    this.isLoading = true;

    this.customerManagerService.getModuleConfigs(this.searchTerm, this.currentPage, this.itemsPerPage)
      .subscribe({
        next: (response) => {
          this.moduleConfigs = response.data;
          this.filteredModuleConfigs = response.data;
          this.totalItems = response.total;
          this.isLoading = false;
        },
        error: (error) => {
          console.error('載入模組配置資料時發生錯誤:', error);
          this.isLoading = false;
          alert('載入模組配置資料失敗: ' + error);
        }
      });
  }

  /**
   * 套用搜尋篩選
   */
  applyFilters(): void {
    this.currentPage = 1;
    this.loadModuleConfigs();
  }

  /**
   * 獲取當前頁面的項目
   */
  getCurrentPageItems(): ModuleConfig[] {
    // 由於我們現在使用後端分頁，直接返回已篩選的資料
    return this.filteredModuleConfigs;
  }

  /**
   * 獲取總頁數
   */
  getTotalPages(): number {
    return Math.ceil(this.totalItems / this.itemsPerPage);
  }

  /**
   * 編輯模組配置
   */
  editModuleConfig(moduleConfig: ModuleConfig): void {
    this.selectedModuleConfig = moduleConfig;
    this.isEditing = true;
    this.populateForm(moduleConfig);
    this.showModal = true;
  }

  /**
   * 填充表單資料
   */
  populateForm(moduleConfig: ModuleConfig): void {
    this.moduleForm.patchValue({
      companyid: moduleConfig.companyid,
      show_nopermission: moduleConfig.show_nopermission,
      departments_introduce: moduleConfig.departments?.introduce || ''
    });

    // 填充 departments
    this.clearDepartmentsFormArray();
    if (moduleConfig.departments?.items) {
      moduleConfig.departments.items.forEach(dept => {
        this.addDepartmentFormGroup(dept);
      });
    }

    // 填充 lobby
    this.clearLobbyFormArray();
    if (moduleConfig.lobby) {
      moduleConfig.lobby.forEach(assistant => {
        this.addLobbyFormGroup(assistant);
      });
    }
  }

  /**
   * 清空 departments FormArray
   */
  clearDepartmentsFormArray(): void {
    while (this.departmentsFormArray.length !== 0) {
      this.departmentsFormArray.removeAt(0);
    }
  }

  /**
   * 清空 lobby FormArray
   */
  clearLobbyFormArray(): void {
    while (this.lobbyFormArray.length !== 0) {
      this.lobbyFormArray.removeAt(0);
    }
  }

  /**
   * 新增 department FormGroup
   */
  addDepartmentFormGroup(department?: DepartmentItem): void {
    const departmentGroup = this.fb.group({
      dep_id: [department?.dep_id || this.generateDepartmentId(), Validators.required],
      dep_name: [department?.dep_name || '', Validators.required],
      dep_image_url: [department?.dep_image_url || '', Validators.required],
      dep_content: [department?.dep_content || '', Validators.required]
    });

    this.departmentsFormArray.push(departmentGroup);
  }

  /**
   * 移除 department FormGroup
   */
  removeDepartmentFormGroup(index: number): void {
    this.departmentsFormArray.removeAt(index);
  }

  /**
   * 新增 lobby FormGroup
   */
  addLobbyFormGroup(assistant?: AssistantItem): void {
    const assistantGroup = this.fb.group({
      assistantNo: [assistant?.assistantNo || '', Validators.required],
      title: [assistant?.title || '', Validators.required],
      content: [assistant?.content || '', Validators.required],
      enable: [assistant?.enable !== undefined ? assistant.enable : true]
    });

    this.lobbyFormArray.push(assistantGroup);
  }

  /**
   * 移除 lobby FormGroup
   */
  removeLobbyFormGroup(index: number): void {
    this.lobbyFormArray.removeAt(index);
  }

  /**
   * 儲存模組配置
   */
  saveModuleConfig(): void {
    if (this.moduleForm.valid && this.selectedModuleConfig?.id) {
      const formValue = this.moduleForm.value;

      const moduleConfigData: Partial<ModuleConfig> = {
        companyid: formValue.companyid,
        show_nopermission: formValue.show_nopermission,
        departments: {
          introduce: formValue.departments_introduce,
          items: formValue.departments
        },
        lobby: formValue.lobby
      };

      // 更新現有模組配置
      this.customerManagerService.updateModuleConfig(this.selectedModuleConfig.id, moduleConfigData)
        .subscribe({
          next: (updatedConfig) => {
            this.loadModuleConfigs();
            this.closeModal();
            alert('模組配置更新成功！');
            this.moduleUpdated.emit(); // 發射模組更新事件
          },
          error: (error) => {
            console.error('更新模組配置時發生錯誤:', error);
            alert('模組配置更新失敗: ' + error);
          }
        });
    } else {
      alert('請填寫所有必要欄位');
    }
  }

  /**
   * 關閉模態框
   */
  closeModal(): void {
    this.showModal = false;
    this.selectedModuleConfig = null;
    this.moduleForm.reset();
    this.clearDepartmentsFormArray();
    this.clearLobbyFormArray();
  }

  /**
   * 生成部門ID
   */
  generateDepartmentId(): string {
    return 'dept-' + Date.now().toString();
  }

  /**
   * 搜尋變更處理
   */
  onSearchChange(): void {
    this.applyFilters();
  }

  /**
   * 頁面變更處理
   */
  onPageChange(page: number): void {
    this.currentPage = page;
    this.loadModuleConfigs();
  }

  /**
   * 獲取啟用的助手數量
   */
  getEnabledAssistantsCount(lobby: AssistantItem[] | undefined): number {
    if (!lobby) return 0;
    return lobby.filter(assistant => assistant.enable).length;
  }
}
